import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { TESTIMONIALS } from "@/data/content";

function TestimonialCard({ t, index }) {
  return (
    <div
      data-testid={`testimonial-${index}`}
      className="flex-[0_0_100%] md:flex-[0_0_85%] lg:flex-[0_0_70%] bg-white border border-neutral-200 p-6 sm:p-10 lg:p-14"
    >
      <div className="flex items-start justify-between gap-4 sm:gap-6 mb-6 sm:mb-10">
        <span className="font-serif text-5xl sm:text-7xl text-gold leading-none">&ldquo;</span>
        {t.detail && (
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.22em] text-gold/80 text-right max-w-[180px]">
            {t.detail}
          </span>
        )}
      </div>
      <p className="font-serif text-xl sm:text-2xl lg:text-3xl leading-snug text-neutral-900">
        {t.quote}
      </p>
      <div className="mt-8 sm:mt-10 flex items-center gap-4">
        <img
          src={t.avatar}
          alt={t.name}
          className="w-12 h-12 rounded-full object-cover grayscale"
        />
        <div className="flex-1">
          <div className="text-neutral-900">{t.name}</div>
          <div className="text-sm text-neutral-500">{t.role}</div>
        </div>
        {t.sector && (
          <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] text-neutral-400 border border-neutral-200 px-3 py-1">
            {t.sector}
          </span>
        )}
      </div>
    </div>
  );
}

function CarouselControls({ emblaApi }) {
  return (
    <div className="flex gap-3">
      <button
        onClick={() => emblaApi?.scrollPrev()}
        data-testid="testimonial-prev"
        aria-label="Previous testimonial"
        className="w-12 h-12 border border-neutral-200 hover:border-gold hover:text-gold transition flex items-center justify-center"
      >
        <ArrowLeft size={16} />
      </button>
      <button
        onClick={() => emblaApi?.scrollNext()}
        data-testid="testimonial-next"
        aria-label="Next testimonial"
        className="w-12 h-12 border border-neutral-200 hover:border-gold hover:text-gold transition flex items-center justify-center"
      >
        <ArrowRight size={16} />
      </button>
    </div>
  );
}

function CarouselDots({ items, selected, emblaApi }) {
  return (
    <div className="flex gap-2 mt-10">
      {items.map((t, i) => (
        <button
          key={`dot-${t.name}`}
          onClick={() => emblaApi?.scrollTo(i)}
          data-testid={`testimonial-dot-${i}`}
          aria-label={`Go to testimonial ${i + 1}`}
          aria-current={selected === i}
          className={`h-px transition-all ${
            selected === i ? "w-12 bg-gold" : "w-6 bg-neutral-100"
          }`}
        />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
    // `setSelected` is a stable React setter - does not need to be listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section
      data-testid="testimonials-section"
      className="relative py-20 sm:py-28 lg:py-40 px-5 sm:px-6 lg:px-12 border-y border-neutral-200/70 bg-[#FAFAFA]"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-5 sm:gap-6 mb-10 sm:mb-16">
          <div>
            <p className="overline">Proof, not promises</p>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-5 sm:mt-6 max-w-[18ch]">
              Real founders. Real <span className="italic text-aurora">studios.</span>
            </h2>
          </div>
          <CarouselControls emblaApi={emblaApi} />
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6">
            {TESTIMONIALS.map((t, i) => (
              <TestimonialCard key={t.name} t={t} index={i} />
            ))}
          </div>
        </div>

        <CarouselDots items={TESTIMONIALS} selected={selected} emblaApi={emblaApi} />
      </div>
    </section>
  );
}
